import React from 'react'
import type { CustomRenewalPreview } from '@homevest/types/renewals'
import { Link } from 'react-router-dom'
import { Badge, Box, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import { agreementSigners } from '@homevest/utils'

import { combineFirstNameLastName } from 'lib/utils'
import { formatUTCDate } from 'lib/date-time'
import { formatMoney } from 'lib/numbers'
import { RenewalDetails } from 'types/Renewals'
import { RenewalRecurringFeesReview } from './RenewalBillingReview'

const { ROLES } = agreementSigners

const ReviewCard = ({ children, title }: React.PropsWithChildren<{ title: string }>) => (
  <Box borderColor='gray.200' borderRadius='lg' borderWidth='1px' p={5}>
    <Text fontSize='md' fontWeight='semibold' mb={4}>
      {title}
    </Text>
    <Stack spacing={3}>{children}</Stack>
  </Box>
)

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Text color='gray.500' fontSize='xs' fontWeight='semibold' textTransform='uppercase'>
      {label}
    </Text>
    <Box mt={0.5}>{value}</Box>
  </Box>
)

const RenewRentalConfirmationDetails = ({
  renewalDetails,
  billingPreview
}: {
  renewalDetails: RenewalDetails
  billingPreview?: CustomRenewalPreview
}) => {
  const isKCPOA = renewalDetails.leaseVariant === 'kcpoa'

  return (
    <Stack spacing={5}>
      <Box bg='gray.50' borderColor='gray.200' borderRadius='lg' borderWidth='1px' p={5}>
        <HStack spacing={3} wrap='wrap'>
          <Text fontSize='lg' fontWeight='semibold'>
            {renewalDetails.fullAddress}
          </Text>
          <Badge colorScheme={isKCPOA ? 'teal' : 'blue'}>
            {isKCPOA
              ? 'RPP'
              : billingPreview?.template.kind === 'sandalwood'
              ? 'Sandalwood'
              : 'Standard'}
          </Badge>
        </HStack>
        <Text color='gray.600' fontSize='sm' mt={2}>
          {formatUTCDate(renewalDetails.newLeaseStartDate, 'MMM d, yyyy')} –{' '}
          {formatUTCDate(renewalDetails.newLeaseEndDate, 'MMM d, yyyy')} ·{' '}
          {renewalDetails.extensionMonthsInDigits}{' '}
          {renewalDetails.extensionMonthsInDigits === 1 ? 'month' : 'months'}
        </Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <ReviewCard title='Lease terms'>
          <DetailRow
            label='Monthly rent'
            value={formatMoney(renewalDetails.newLeaseRent, 2, '$')}
          />
          <DetailRow label='Landlord' value={renewalDetails.landlordName || 'Not available'} />
          <DetailRow label='Lease template' value={renewalDetails.agreementTemplateName} />
          <DetailRow
            label='Original lease began'
            value={formatUTCDate(renewalDetails.originalLeaseDate, 'MMM d, yyyy')}
          />
          <DetailRow
            label='Renewal period'
            value={`${formatUTCDate(
              renewalDetails.newLeaseStartDate,
              'MMM d, yyyy'
            )} – ${formatUTCDate(renewalDetails.newLeaseEndDate, 'MMM d, yyyy')}`}
          />
        </ReviewCard>

        {billingPreview && !isKCPOA ? (
          <RenewalRecurringFeesReview
            preview={billingPreview}
            requestedOptionPremium={renewalDetails.optionPremium}
          />
        ) : (
          <ReviewCard title={isKCPOA ? 'Purchase-option terms' : 'Lease fees'}>
            {isKCPOA ? (
              <>
                <DetailRow
                  label='Monthly option fee'
                  value={formatMoney(renewalDetails.kcpoaTerms.monthlyOptionFee, 2, '$')}
                />
                <DetailRow
                  label='Initial option fee'
                  value={formatMoney(renewalDetails.kcpoaTerms.initialOptionFee, 2, '$')}
                />
                <DetailRow
                  label='Deposit credit'
                  value={formatMoney(renewalDetails.kcpoaTerms.depositCredit, 2, '$')}
                />
                <DetailRow
                  label='Net initial option fee'
                  value={formatMoney(
                    renewalDetails.kcpoaTerms.initialOptionFee -
                      renewalDetails.kcpoaTerms.depositCredit,
                    2,
                    '$'
                  )}
                />
                <DetailRow
                  label='Purchase price'
                  value={formatMoney(renewalDetails.kcpoaTerms.purchasePrice, 2, '$')}
                />
              </>
            ) : (
              <DetailRow
                label='Monthly option premium'
                value={formatMoney(renewalDetails.optionPremium, 2, '$')}
              />
            )}
          </ReviewCard>
        )}

        <ReviewCard title='Signers'>
          {renewalDetails.signers.length ? (
            renewalDetails.signers.map(({ role, user }) =>
              user ? (
                <Box key={user.id}>
                  <Link to={`/users/${user.id}`} target='_blank' rel='noopener noreferrer'>
                    {combineFirstNameLastName(user)}
                  </Link>
                  <Text color='gray.500' fontSize='sm'>
                    {role === ROLES.COSIGNER ? 'Cosigner from current lease' : 'Active rental user'}
                  </Text>
                </Box>
              ) : null
            )
          ) : (
            <Text color='red.600'>No active rental users.</Text>
          )}
        </ReviewCard>

        {!billingPreview && (
          <ReviewCard title='Recurring add-ons'>
            {renewalDetails.addOns.length ? (
              [...renewalDetails.addOns]
                .sort((left, right) =>
                  left.liability_type_name.localeCompare(right.liability_type_name)
                )
                .map((addOn) => (
                  <Box key={addOn.liability_type_name}>
                    <Text>{addOn.liability_type_name}</Text>
                    <Text color='gray.500' fontSize='sm'>
                      {formatMoney(addOn.price, 2, '$')}/month
                    </Text>
                  </Box>
                ))
            ) : (
              <Text color='gray.500'>None</Text>
            )}
          </ReviewCard>
        )}
      </SimpleGrid>

      {renewalDetails.notes.trim() && (
        <ReviewCard title='Renewal note'>
          <Text whiteSpace='pre-wrap'>{renewalDetails.notes}</Text>
        </ReviewCard>
      )}
    </Stack>
  )
}

export default RenewRentalConfirmationDetails
